<script lang="ts">
  import { ApiError, type Conversation } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { render } from '../lib/markdown';
  import { messagePath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let { conversation, onClose }: { conversation: Conversation; onClose: () => void } = $props();

  let error = $state('');
  const pinned = $derived(app.pinned[conversation.id]);
  const archived = $derived(!!conversation.archived_at);

  $effect(() => {
    void app.loadPinned(conversation.id).catch(() => (error = 'Impossible de charger les messages épinglés'));
  });

  async function unpin(id: string) {
    const message = pinned?.find((m) => m.id === id);
    if (!message) return;
    try {
      await app.togglePin(message);
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }
</script>

<aside class="panel" aria-label="Messages épinglés">
  <header>
    <h2>Épinglés <span class="where">· {conversation.kind === 'public' || conversation.kind === 'private' ? `# ${conversation.title}` : conversation.title}</span></h2>
    <button type="button" class="close" aria-label="Fermer les épinglés" onclick={onClose}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </header>
  <div class="list">
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if !pinned}
      <p class="status">Chargement…</p>
    {:else}
      {#each pinned as message (message.id)}
        <article class="card">
          <div class="meta">
            <Avatar user={message.author} size={24} />
            <span class="author">{message.author.display_name}</span>
            <time datetime={message.created_at}>{dayAndTime(message.created_at)}</time>
          </div>
          <div class="body">{@html render(message.body, app.me?.username ?? '')}</div>
          <p class="by">{message.pinned_by ? `Épinglé par ${message.pinned_by.display_name}` : 'Épinglé'}</p>
          <div class="actions">
            <button type="button" class="link" onclick={() => router.go(messagePath(conversation.id, message.id))}>Voir</button>
            {#if !archived}<button type="button" class="link" onclick={() => unpin(message.id)}>Désépingler</button>{/if}
          </div>
        </article>
      {:else}
        <p class="status">Aucun message épinglé. Épinglez un message depuis son menu pour le retrouver ici.</p>
      {/each}
    {/if}
    <p class="note">
      {archived ? 'Ce canal est archivé : les épinglés ne peuvent plus changer.' : 'Tous les membres peuvent épingler ou désépingler un message.'}
    </p>
  </div>
</aside>

<style>
  .panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-page);
    border-left: 1px solid var(--border);
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--header-h);
    padding: 8px 12px 8px 16px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  h2 {
    flex: 1;
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .where {
    color: var(--muted);
    font-weight: 400;
    font-size: 14px;
  }
  .close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .author {
    font-weight: 600;
  }
  time {
    font-size: 12px;
    color: var(--muted);
  }
  .body {
    overflow-wrap: anywhere;
    max-height: 120px;
    overflow: hidden;
  }
  .body :global(p) {
    margin: 0;
  }
  .by {
    margin: 0;
    font-size: 12px;
    color: var(--accent-on-soft);
  }
  .actions {
    display: flex;
    gap: 16px;
  }
  .link {
    border: 0;
    background: none;
    padding: 0;
    color: var(--accent-text);
    font-weight: 600;
    font-size: 13px;
  }
  .status,
  .note {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
</style>
